import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { logout } from "../../auth/verifyAuth";
import { useNavigate, useLocation } from "react-router-dom";
import { ThemeContext, TypesContext, HubspotAccountContext } from "../../App";
import { FormTitleContext } from "./Global";
import { MsalInstanceContext, VariablesContext } from "../../auth/AppProvider";
import { Disclosure, DisclosureButton, DisclosurePanel, Menu, MenuButton, MenuItems, MenuItem } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { triggerGoogleTranslate, restoreOriginalLanguage } from "../../utils/googleTranslate";
import "./tabs.css";

function hexToRgba(hex, alpha) {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function Aside() {
  const { t, i18n } = useTranslation();
  let navigate = useNavigate();
  let location = useLocation();
  const theme = useContext(ThemeContext);
  const [formTitle,] = useContext(FormTitleContext);
  const [typesParameters, setTypesParameters] = useContext(TypesContext);
  const msalInstance = useContext(MsalInstanceContext);
  const profileHubspot = useContext(HubspotAccountContext);
  const variables = useContext(VariablesContext);
  const [currentLang, setCurrentLang] = useState(i18n.language?.substring(0, 2) || 'fr');

  useEffect(() => {
    var one_is_selected = typesParameters.find(t => t.selected === true);
    if (!one_is_selected) {
      setTypesParameters(tp => tp.map((t, k) => { t.selected = k === 0 ? true : false; return t }))
    }
  }, [typesParameters])

  const account = profileHubspot.firstName ?? msalInstance?.getAllAccounts()?.[0];
  const givenName = profileHubspot.firstname || account?.idTokenClaims?.given_name || account?.name?.split(' ')?.[0] || '';
  const familyName = profileHubspot.lastname || account?.idTokenClaims?.family_name || account?.name?.split(' ')?.slice(1).join(' ') || '';
  const fullName = [givenName, familyName].filter(Boolean).join(' ') || profileHubspot.name || '';
  const email = profileHubspot.email || '';
  const initials = ((givenName?.[0] || '') + (familyName?.[0] || '')).toUpperCase() || fullName?.substring(0, 2).toUpperCase();

  const primaryColor = theme?.primary_color || '#6366f1';
  const primaryFaded = hexToRgba(primaryColor, 0.12);

  const selectedType = typesParameters?.find(t => t.selected);
  const canCreateNewCompletion = selectedType?.canCreateNewCompletion === true;

  const clientName = variables?.ClientName || '';
  const schoolInitials = clientName.substring(0, 2).toUpperCase();

  // const changeLanguage = (lang) => {
  //   i18n.changeLanguage(lang);
  //   localStorage.setItem('Language', lang);
  //   setCurrentLang(lang);

  //   if (lang === "en") {
  //     triggerGoogleTranslate("en");
  //   } else {
  //     triggerGoogleTranslate("fr");
  //   }
  // };

  const handleLogout = () => {
    if (location.pathname !== "/") navigate("/");
    logout(msalInstance);
  };

  return <Disclosure as="nav">
    {({ open }) => (
      <>
        <header className="border-b border-gray-100" style={{ backgroundColor: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(12px)' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between" style={{ height: '67px' }}>

              {/* Left: logo + school name */}
              <div className="flex items-center gap-3 cursor-pointer flex-shrink-0"
                onClick={() => window.location.replace(window.location.origin + '/')}>
                {theme?.header_logo ? (
                  <img
                    className="h-10 w-auto max-w-[160px] object-contain"
                    src={"data" + theme.header_logo.split(";data")[1]}
                    alt={theme.header_logo.split(';')[0]}
                  />
                ) : (
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                    style={{ backgroundColor: primaryColor }}>
                    {schoolInitials}
                  </div>
                )}
                {theme?.header_logo ? null : <span className="hidden sm:block font-semibold text-gray-900 text-base leading-tight">
                  {clientName}
                </span>}
              </div>

              {/* Center: navigation tabs */}
              <nav className="hidden sm:flex items-center gap-1">
                {canCreateNewCompletion && <button
                  onClick={() => navigate('/newform')}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                    location.pathname === '/newform'
                      ? 'bg-gray-100 text-gray-900'
                      : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                  }`}>
                  {t("Formations")}
                </button>}
                <button
                  onClick={() => navigate('/')}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                    location.pathname === '/'
                      ? 'bg-gray-100 text-gray-900'
                      : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                  }`}>
                  {t("Mes candidatures")}
                </button>
              </nav>

              {/* Right: language switcher + user menu (desktop) */}
              <div className="hidden sm:flex items-center gap-3">

                {/* Language switcher */}
                {/*<div className="flex items-center gap-0.5 bg-gray-100 rounded-full px-1 py-1">
                  {['FR', 'EN'].map((lang) => (
                    <button
                      id={lang}
                      key={lang}
                      onClick={() => changeLanguage(lang.toLowerCase())}
                      translate="no"
                      className="text-xs font-semibold px-3 py-1 rounded-full transition-all duration-150"
                      style={currentLang === lang.toLowerCase()
                        ? { backgroundColor: '#fff', color: '#111', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }
                        : { color: '#9ca3af' }}>
                      {lang}
                    </button>
                  ))}
                </div> */}

                {/* User dropdown */}
                <Menu as="div" className="relative">
                  <MenuButton className="flex items-center gap-2 px-3 py-1.5 rounded-full hover:bg-gray-50 transition-colors cursor-pointer focus:outline-none">
                    <div
                      className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: primaryFaded, color: primaryColor }}>
                      {initials}
                    </div>
                    <span className="text-sm font-medium text-gray-700">{givenName}</span>
                  </MenuButton>

                  <MenuItems
                    anchor="bottom end"
                    className="z-50 w-64 rounded-xl bg-white shadow-lg ring-1 ring-gray-100 focus:outline-none p-1.5 mt-1">
                    {/* User info block */}
                    <div className="px-3 py-2.5 mb-1 border-b border-gray-50">
                      <p className="text-sm font-semibold text-gray-900">{fullName}</p>
                      <p className="text-xs text-gray-400 mt-0.5 truncate">{email}</p>
                    </div>

                    {/* <MenuItem>
                      {({ focus }) => (
                        <button
                          onClick={() => navigate('/profile')}
                          className={`w-full flex items-center justify-between text-left px-3 py-2 text-sm rounded-lg transition-colors ${focus ? 'bg-gray-50' : ''}`}>
                          <span className="text-gray-700">{t("Mon profil")}</span>
                        </button>
                      )}
                    </MenuItem> */}

                    <MenuItem>
                      {({ focus }) => (
                        <button
                          onClick={handleLogout}
                          className={`w-full flex items-center gap-2 text-left px-3 py-2 text-sm rounded-lg transition-colors ${focus ? 'bg-gray-50' : ''}`}>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          <span className="text-gray-700">{t("Se déconnecter")}</span>
                        </button>
                      )}
                    </MenuItem>
                  </MenuItems>
                </Menu>
              </div>

              {/* Mobile: hamburger */}
              <div className="flex items-center sm:hidden">
                <DisclosureButton className="inline-flex items-center justify-center rounded-md p-2 text-gray-500 hover:text-gray-700 focus:outline-none">
                  <span className="sr-only">{t("Open main menu")}</span>
                  {open ? (
                    <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6">
                      <path fillRule="evenodd" d="M3 6.75A.75.75 0 0 1 3.75 6h16.5a.75.75 0 0 1 0 1.5H3.75A.75.75 0 0 1 3 6.75ZM3 12a.75.75 0 0 1 .75-.75h16.5a.75.75 0 0 1 0 1.5H3.75A.75.75 0 0 1 3 12Zm0 5.25a.75.75 0 0 1 .75-.75h16.5a.75.75 0 0 1 0 1.5H3.75a.75.75 0 0 1-.75-.75Z" clipRule="evenodd" />
                    </svg>
                  )}
                </DisclosureButton>
              </div>
            </div>
          </div>
        </header>

        {/* Mobile panel */}
        <DisclosurePanel className="sm:hidden border-b border-gray-100 bg-white">
          <div className="px-4 py-3 space-y-1">
            {/* Mobile navigation tabs */}
            {canCreateNewCompletion && <button
              onClick={() => navigate('/newform')}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium ${
                location.pathname === '/newform' ? 'bg-gray-100 text-gray-900' : 'text-gray-500'
              }`}>
              {t("Formations")}
            </button>}
            <button
              onClick={() => navigate('/')}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium ${
                location.pathname === '/' ? 'bg-gray-100 text-gray-900' : 'text-gray-500'
              }`}>
              {t("Mes candidatures")}
            </button>

            {typesParameters?.length > 1 && !formTitle && typesParameters.map(type => (
              <DisclosureButton
                key={type.name}
                as="div"
                className={`cursor-pointer py-2 px-3 rounded-lg text-sm font-medium ${type.selected ? 'text-gray-900 bg-gray-100' : 'text-gray-500'}`}
                onClick={() => {
                  var types = typesParameters;
                  setTypesParameters(types.map(t => { t.selected = t.name === type.name; return t; }));
                }}>
                {type.name}
              </DisclosureButton>
            ))}

            {/* Mobile language switcher */}
            {/* <div className="flex items-center gap-0.5 bg-gray-100 rounded-full px-1 py-1 w-fit">
              {['FR', 'EN'].map((lang) => (
                <button
                  key={lang}
                  onClick={() => changeLanguage(lang.toLowerCase())}
                  className="text-xs font-semibold px-3 py-1 rounded-full transition-all duration-150"
                  style={currentLang === lang.toLowerCase()
                    ? { backgroundColor: '#fff', color: '#111', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }
                    : { color: '#9ca3af' }}>
                  {lang}
                </button>
              ))}
            </div> */}

            {/* Mobile user info + actions */}
            <div className="border-t border-gray-100 pt-3">
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="h-9 w-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                  style={{ backgroundColor: primaryFaded, color: primaryColor }}>
                  {initials}
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{fullName}</p>
                  <p className="text-xs text-gray-400 truncate">{email}</p>
                </div>
              </div>
              {/* <button
                onClick={() => navigate('/profile')}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-50 mb-1">
                {t("Mon profil")}
              </button> */}
              <button
                onClick={handleLogout}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-50">
                {t("Se déconnecter")}
              </button>
            </div>
          </div>
        </DisclosurePanel>
      </>
    )}
  </Disclosure>
}

export default Aside;
